import type { AuthenticatedState } from '../utils/auth/index.js';
import type { Cache, CacheFactory, Caches } from './use-cache.types.js';

export const DEFAULT_ID_SERIALIZER = (item: unknown): string => String(item);

export const createCaches = <D, C extends Cache>(
  authState: AuthenticatedState,
  definitions: Record<string, D>,
  factory: CacheFactory<D, C>,
): Caches<C> => {
  const keys = Object.keys(definitions);
  const caches = keys.reduce(
    (acc, type) => {
      const definition = definitions[type];
      if (!definition) {
        throw new Error(`Cache definition for ${type} is not defined`);
      }

      return {
        // biome-ignore lint/performance/noAccumulatingSpread: how it's supposed to be
        ...acc,
        [type]: factory(definition, authState),
      };
    },
    {} as Caches<C>,
  );
  return caches;
};

/**
 * Whether two auth states may share caches.
 *
 * The organization matters as much as the timestamp: switching organization
 * can issue a token in the same second as the last one, and reusing the caches
 * would show the previous organization's roster and patrols.
 */
export const compareAuthStates = (
  authState1: AuthenticatedState,
  authState2: AuthenticatedState,
): boolean => {
  return (
    authState1.timestamp === authState2.timestamp &&
    authState1.userInfo.organizationId === authState2.userInfo.organizationId
  );
};
